import { FC } from 'react';
import styled from 'styled-components';
import { Box } from '../Box/Box';
import { Gap } from '../Gap/Gap';
import { Button } from '../Button/Button';
import { LinkButton } from '../LinkButton/LinkButton';
import { ButtonToolbar } from '../ButtonToolbar/ButtonToolbar';

const SaveBarContent = styled.div`
  width: 100%;
  display: flex;
`;

const SaveBarRightElement = styled.div<{ paddingRight?: string }>`
  display: flex;
  justify-content: flex-end;
  flex: 1;
  padding-right: ${({ paddingRight }) => paddingRight ?? '40px'};
`;

const SaveBarLeftElement = styled.div<{ paddingLeft?: string }>`
  display: flex;
  justify-content: flex-start;
  flex: 1;
  padding-left: ${({ paddingLeft }) => paddingLeft ?? '40px'};
`;

export interface ISaveBarProps {
  /** Event handler fired when discard button is clicked. */
  onDiscardButtonClicked: () => void;
  /** Optional Discard button label. Defaults to 'Discard Changes' */
  discardButtonLabel?: string;
  /** Event handler fired when exit button is clicked. */
  onExitButtonClicked?: () => void;
  /** Optional Exit button label. Defaults to 'Exit' */
  exitButtonLabel?: string;
  /** Event handler fired when save button is clicked. */
  onSaveButtonClicked: () => void;
  /** Optional Save button label. Defaults to 'Save' */
  saveButtonLabel?: string;
  /** Enable save button. Defaults to false. */
  enableSaveButton?: boolean;
  /** Enables discard button and disabled save button. */
  hasValidationFailed?: boolean;
  /** Child elements to show on the left hand side of the savebar. */
  shortcutOptions?: JSX.Element;
  /** Padding to the left of the SaveBar. */
  paddingLeft?: string;
  /** Padding to the right of the SaveBar. */
  paddingRight?: string;
}

export const SaveBar: FC<ISaveBarProps> = ({
  discardButtonLabel,
  enableSaveButton,
  exitButtonLabel,
  hasValidationFailed,
  onDiscardButtonClicked,
  onExitButtonClicked,
  onSaveButtonClicked,
  saveButtonLabel,
  shortcutOptions,
  paddingLeft,
  paddingRight,
}) => {
  const validationFailed = hasValidationFailed ?? false;
  const discardEnabled = enableSaveButton;
  const saveEnabled = validationFailed ? false : enableSaveButton;

  return (
    <SaveBarContent data-testid="SaveBar">
      {shortcutOptions && (
        <SaveBarLeftElement data-testid="SaveBarLeft" paddingLeft={paddingLeft}>
          <Box verticalPositioning="center" horizontalPositioning="center">
            {shortcutOptions}
            <Gap right={10} />
          </Box>
        </SaveBarLeftElement>
      )}

      <SaveBarRightElement data-testid="SaveBarRight" paddingRight={paddingRight}>
        <ButtonToolbar>
          {onExitButtonClicked && (
            <LinkButton data-testid="ExitButton" onClick={onExitButtonClicked}>
              {exitButtonLabel ? exitButtonLabel : 'Cancel & Exit'}
            </LinkButton>
          )}

          <Button
            data-testid="DiscardButton"
            variation="secondary"
            onClick={onDiscardButtonClicked}
            disabled={!discardEnabled}
          >
            {discardButtonLabel ? discardButtonLabel : 'Discard Changes'}
          </Button>

          <Button data-testid="SaveButton" variation="primary" disabled={!saveEnabled} onClick={onSaveButtonClicked}>
            {saveButtonLabel ? saveButtonLabel : 'Save'}
          </Button>
        </ButtonToolbar>
      </SaveBarRightElement>
    </SaveBarContent>
  );
};

export default SaveBar;
